import React from "react";
import { AlertTriangle, RefreshCw, LogOut } from "lucide-react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Application error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleLogout = async () => {
    try {
      await fetch("/api/auth/signout", {
        method: "POST",
        credentials: "same-origin",
      });
    } catch {
      // ignore
    }
    window.location.href = "/login";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center px-4"
          style={{ backgroundColor: "#0F172A" }}
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div
              className="px-6 py-6 md:px-8 md:py-8 text-center"
              style={{ backgroundColor: "#1B2A4A" }}
            >
              <div className="flex flex-col items-center">
                <img
                  src="/logo.png"
                  alt="Waves"
                  className="h-12 w-auto rounded-lg mb-2"
                />
                <div
                  className="text-sm font-medium mt-0.5"
                  style={{ color: "#65ADDD" }}
                >
                  Partnerportaal
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8 md:px-8">
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
                  <AlertTriangle size={28} className="text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-2">
                  Er is iets misgegaan
                </h2>
                <p className="text-sm text-slate-600 mb-6">
                  We konden de pagina niet laden. Dit kan komen door een
                  verlopen sessie of een tijdelijke storing. Probeer de pagina
                  te vernieuwen of opnieuw in te loggen.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <button
                    onClick={this.handleReload}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all duration-150 hover:opacity-90 active:scale-[0.98] min-h-[44px]"
                    style={{ backgroundColor: "#65ADDD" }}
                  >
                    <RefreshCw size={16} />
                    Probeer opnieuw
                  </button>
                  <button
                    onClick={this.handleLogout}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all duration-150 active:scale-[0.98] min-h-[44px]"
                  >
                    <LogOut size={16} />
                    Opnieuw inloggen
                  </button>
                </div>

                {this.state.error?.message && (
                  <details className="mt-6 w-full text-left">
                    <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
                      Technische details
                    </summary>
                    <pre className="mt-2 p-3 bg-slate-50 rounded-lg text-xs text-slate-600 overflow-x-auto">
                      {this.state.error.message}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
